import { useCallback } from 'react';
import { isColumnGroup } from '../../domain';
import type { ColumnDef, ResolvedColumn } from '../../domain';

function groupKey(id: string | undefined, header: string | undefined): string {
    return id ?? header ?? '';
}

export function collapsibleGroupKeys<T>(defs: ColumnDef<T>[]): Set<string> {
    const keys = new Set<string>();
    defs.forEach((def) => {
        if (
            isColumnGroup(def) &&
            def.collapsible !== false &&
            def.columns.length > 2
        ) {
            keys.add(groupKey(def.id, def.header));
        }
    });
    return keys;
}

export function filterCollapsedColumns<T>(
    columns: ResolvedColumn<T>[],
    isCollapsed: (key: string) => boolean,
): ResolvedColumn<T>[] {
    const out: ResolvedColumn<T>[] = [];
    let runKey = '';
    let runIndex = 0;
    columns.forEach((col) => {
        const key = groupKey(col.groupId, col.groupHeader);
        if (key !== runKey) {
            runKey = key;
            runIndex = 0;
        }
        if (key === '' || !isCollapsed(key) || runIndex < 2) {
            out.push(col);
        }
        runIndex += 1;
    });
    return out;
}

export interface ColumnGroupsState {
    isCollapsed: (key: string) => boolean;
    toggle: (key: string) => void;
}

export interface UseColumnGroupsArgs {
    collapsed?: Record<string, boolean>;
    onToggle: (key: string, collapsed: boolean) => void;
}

export function useColumnGroups(args: UseColumnGroupsArgs): ColumnGroupsState {
    const { collapsed, onToggle } = args;

    const isCollapsed = useCallback(
        (key: string) => Boolean(collapsed?.[key]),
        [collapsed],
    );

    const toggle = useCallback(
        (key: string) => {
            onToggle(key, !isCollapsed(key));
        },
        [isCollapsed, onToggle],
    );

    return { isCollapsed, toggle };
}
